/**
 * Section 6 — the last read-out before publishing.
 *
 * Everything here is derived from state the user has already set. It exists
 * because publishing is irreversible on five networks at once: this is the
 * screen that has to make "you are about to post to X, Y and Z" unmissable, and
 * the status row is what refuses when something is wrong.
 */

import { AlertCircle, CircleCheck } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useTranslation } from '../../../context/LanguageContext';
import type { PlatformId } from '../../../config/platforms';
import type { ComposerMedia, ComposerValidation, ScheduleOptions } from '../../../types/composer';
import { PlatformIcon } from './PlatformIcon';

/** Icons shown inline before the row collapses to a "+N" chip. */
const MAX_PLATFORM_ICONS = 4;

/**
 * Rough per-account publish time. Media upload dominates and runs per network,
 * so the estimate scales with both — it is a range, and labelled as one.
 */
function estimateMinutes(platformCount: number, mediaCount: number): [number, number] {
  if (platformCount === 0) return [0, 0];
  const base = Math.max(1, Math.round(platformCount * 0.4));
  const withMedia = base + Math.round(mediaCount * 0.5);
  return [withMedia, withMedia * 2];
}

interface PublishingSummaryProps {
  selectedPlatforms: PlatformId[];
  text: string;
  media: ComposerMedia[];
  schedule: ScheduleOptions;
  validation: ComposerValidation;
}

export function PublishingSummary({
  selectedPlatforms,
  text,
  media,
  schedule,
  validation,
}: Readonly<PublishingSummaryProps>) {
  const { t } = useTranslation();
  const [minMinutes, maxMinutes] = estimateMinutes(selectedPlatforms.length, media.length);
  const overflow = selectedPlatforms.length - MAX_PLATFORM_ICONS;

  return (
    <div className="space-y-2.5">
      <Row label={t('composer.summary.platforms')}>
        {selectedPlatforms.length === 0 ? (
          <span className="text-slate-400">{t('composer.summary.noPlatforms')}</span>
        ) : (
          <span className="flex items-center gap-1.5">
            {selectedPlatforms.slice(0, MAX_PLATFORM_ICONS).map((platform) => (
              <PlatformIcon key={platform} platform={platform} size={15} brandColor />
            ))}
            {overflow > 0 && (
              <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">
                +{overflow}
              </span>
            )}
          </span>
        )}
      </Row>

      <Row label={t('composer.summary.media')}>
        {t('composer.summary.files', { count: media.length })}
      </Row>

      <Row label={t('composer.summary.totalChars')}>
        <span className="tabular-nums">{text.length.toLocaleString()}</span>
      </Row>

      <Row label={t('composer.summary.estimatedTime')}>
        {selectedPlatforms.length === 0
          ? t('common.emptyValue')
          : t('composer.summary.minutesRange', { min: minMinutes, max: maxMinutes })}
      </Row>

      <Row label={t('composer.summary.status')}>
        <StatusPill valid={validation.valid} mode={schedule.mode} />
      </Row>

      {!validation.valid && validation.errors.length > 0 && (
        <ul className="space-y-1 rounded-lg bg-rose-50 px-3 py-2">
          {validation.errors.map((error) => (
            <li
              key={`${error.field}-${error.message}`}
              className="flex items-start gap-1.5 text-[11px] leading-snug text-rose-700"
            >
              <AlertCircle className="mt-px h-3 w-3 shrink-0" />
              {error.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Row({ label, children }: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[12px] text-slate-500">{label}</span>
      <span className="text-[12px] font-semibold text-slate-800">{children}</span>
    </div>
  );
}

function StatusPill({
  valid,
  mode,
}: Readonly<{ valid: boolean; mode: ScheduleOptions['mode'] }>) {
  const { t } = useTranslation();

  const label = (() => {
    if (!valid) return t('composer.summary.statusBlocked');
    if (mode === 'scheduled') return t('composer.summary.statusScheduled');
    if (mode === 'draft') return t('composer.summary.statusDraft');
    return t('composer.summary.statusReady');
  })();

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
        valid ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
      )}
    >
      {valid ? <CircleCheck className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
      {label}
    </span>
  );
}
